<template>
  <div>
    <form @submit.prevent="createNanoLoanRate">
      <modal
        v-show="isNanoLoanRateModal"
        @close="closeNanoLoanRateModal"
        footer
      >
        <template v-slot:title>
          <h5>{{ nanoLoanRate.id ? 'Update' : 'Create' }} Nano Loan Rate</h5>
        </template>
        <template v-slot:body>
          <div class="form-group">
            <label for="rank">Rank</label>
            <input
              type="number"
              class="form-control"
              name="rank"
              v-model="nanoLoanRate.rank"
            />
          </div>
          <div class="form-group">
            <label for="minimum_amount">Minimum Amount</label>
            <input
              type="number"
              class="form-control"
              name="minimum_amount"
              v-model="nanoLoanRate.minimum_amount"
            />
          </div>
          <div class="form-group">
            <label for="maximum_amount">Maximum Amount</label>
            <input
              type="number"
              class="form-control"
              name="maximum_amount"
              v-model="nanoLoanRate.maximum_amount"
            />
          </div>
          <div class="form-group">
            <label for="interest_rate">Interest Rate (%)</label>
            <input
              type="number"
              step="0.01"
              class="form-control"
              name="interest_rate"
              v-model="nanoLoanRate.interest_rate"
            />
          </div>
          <div class="form-group">
            <label for="installments">Installments</label>
            <input
              type="number"
              class="form-control"
              name="installments"
              v-model="nanoLoanRate.installments"
            />
          </div>
          <div class="form-group">
            <label for="duration">Duration (days)</label>
            <input
              type="number"
              class="form-control"
              name="duration"
              v-model="nanoLoanRate.duration"
            />
          </div>
          <div class="form-group">
            <label for="level_collections_required">Level Collections Required</label>
            <input
              type="number"
              class="form-control"
              name="level_collections_required"
              v-model="nanoLoanRate.level_collections_required"
            />
          </div>
          <div class="form-group">
            <label for="previous_amount_required">Previous Amount Required</label>
            <input
              type="number"
              class="form-control"
              name="previous_amount_required"
              v-model="nanoLoanRate.previous_amount_required"
            />
          </div>
          <div class="form-group">
            <label for="status">Status</label>
            <select
              class="form-control"
              v-model="nanoLoanRate.status"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </template>
        <template v-slot:footer>
          <button
            type="submit"
            class="button"
            :disabled="creatingNanoLoanRateLoading || !formIsValid"
          >
            <span class="custom-loader" v-if="creatingNanoLoanRateLoading">
              <loader-icon size="1.5x" class="custom-class"></loader-icon>
              <span style="font-weight: 600; text-transform: initial;">
                {{ nanoLoanRate.id ? 'Updating' : 'Creating' }} Nano Loan Rate...
              </span>
            </span>
            <span v-else>
              {{ nanoLoanRate.id ? 'Update' : 'Create' }} Nano Loan Rate
            </span>
          </button>
          <button
            type="button"
            class="btn btn-secondary"
            data-dismiss="modal"
            @click="closeNanoLoanRateModal"
          >
            Cancel
          </button>
        </template>
      </modal>
    </form>
    <loader-ring
      v-if="getNanoLoanRatesLoading"
      class="loader-center"
    ></loader-ring>
    <div v-else>
      <div class="row align-items-center justify-content-between mb-2">
        <div class="col">
          <h1 class="mb-2 font-regular">Nano Loan Rates</h1>
        </div>
        <div class="col-auto">
          <button
            class="button button--outline text-uppercase"
            @click="showNanoLoanRateModal"
          >
            Add Nano Loan Rate
          </button>
        </div>
      </div>

      <div class="card card--no-shadow">
        <div class="card-header">
          <slot name="header">
            <h4 class="card-title">Nano Loan Rates</h4>
          </slot>
        </div>
        <div class="card-content">
          <div class="card-body">
            <CDataTable
              :items="nanoLoanRates"
              :fields="fields"
              :items-per-page="small ? 10 : 5"
              sorter
              tableFilter
            >
              <template #minimum_amount="{item}">
                <td class="table--action-mobile">
                  ₦{{ Number(item.minimum_amount).toLocaleString() }}
                </td>
              </template>
              <template #maximum_amount="{item}">
                <td class="table--action-mobile">
                  ₦{{ Number(item.maximum_amount).toLocaleString() }}
                </td>
              </template>
              <template #interest_rate="{item}">
                <td class="table--action-mobile">
                  {{ item.interest_rate }}%
                </td>
              </template>
              <template #duration="{item}">
                <td class="table--action-mobile">
                  {{ item.duration }} days
                </td>
              </template>
              <template #status="{item}">
                <td class="table--action-mobile">
                  <span class="badge" :class="{
                    'badge-success': item.status === 'active',
                    'badge-secondary': item.status !== 'active'
                  }">
                    {{ item.status }}
                  </span>
                </td>
              </template>
              <template #created_at="{item}">
                <td class="table--action-mobile">
                  {{ item.created_at | date }}
                </td>
              </template>
              <template #action="{item}">
                <td class="table--action-mobile">
                  <button
                    class="button btn-success"
                    @click="showUpdateModal(item)"
                    style="background-color: #0bc681;"
                  >
                    Update
                  </button>
                </td>
              </template>
            </CDataTable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CDataTable from "@/components/CDataTable.vue";
import ApiService from "@/services/api.service";
import ErrorService from "@/services/error.service";
import Swal from "sweetalert2";

const fields = [
  "rank", 
  "minimum_amount", 
  "maximum_amount", 
  "interest_rate", 
  "installments", 
  "duration", 
  "status", 
  "action"
];

export default {
  name: "NanoLoanRates",
  components: {
    CDataTable
  },
  data() {
    return {
      fields,
      small: false,
      getNanoLoanRatesLoading: false,
      isNanoLoanRateModal: false,
      creatingNanoLoanRateLoading: false,
      nanoLoanRates: [],
      nanoLoanRate: {}
    };
  },
  computed: {
    formIsValid() {
      return (
        this.nanoLoanRate.rank !== "" &&
        this.nanoLoanRate.minimum_amount !== "" &&
        this.nanoLoanRate.maximum_amount !== "" &&
        this.nanoLoanRate.interest_rate !== "" &&
        this.nanoLoanRate.installments !== "" &&
        this.nanoLoanRate.duration !== "" &&
        this.nanoLoanRate.level_collections_required !== "" &&
        this.nanoLoanRate.status !== ""
      );
    },
    permissions() {
      let permissions = this.$store.getters.permissions;
      return permissions;
    }
  },
  methods: {
    showUpdateModal(item) {
      this.nanoLoanRate = Object.assign({}, item);
      this.showNanoLoanRateModal();
      console.log(this.nanoLoanRate);
    },
    closeNanoLoanRateModal() {
      this.isNanoLoanRateModal = false;
      this.nanoLoanRate = {};
    },
    showNanoLoanRateModal() {
      this.isNanoLoanRateModal = true;
    },
    async getNanoLoanRates() {
      try {
        this.getNanoLoanRatesLoading = true;
        const response = await ApiService.get("loan/instant/nano_loan_rates");
        this.nanoLoanRates = response.data.data;
        this.getNanoLoanRatesLoading = false;
      } catch (error) {
        this.getNanoLoanRatesLoading = false;
        console.log(error.response);
        if (error.response && error.response.data) {
          ErrorService.displayErrorAlert(error.response.data);
        }
      }
    },
    async createNanoLoanRate() {
      try {
        this.creatingNanoLoanRateLoading = true;
        const endpoint = `loan/instant/create_update_nano_loan_rate`;
        
        const response = await ApiService.post(endpoint, this.nanoLoanRate);

        this.creatingNanoLoanRateLoading = false;
        this.closeNanoLoanRateModal();
        this.getNanoLoanRates();

        Swal.fire({
          title: "Success",
          text: response.data.message || "Nano loan rate saved successfully",
          icon: "success",
          timer: 3000,
          confirmButtonText: "Close",
          showConfirmButton: false
        });
      } catch (error) {
        this.creatingNanoLoanRateLoading = false;
        this.closeNanoLoanRateModal();
        let _error = error.response.data;
        ErrorService.displayErrorAlert(_error);
        console.log(_error);
      }
    }
  },
  mounted() {
    this.getNanoLoanRates();
  }
};
</script>

<style scoped></style>